import { Col, Row } from "antd";
import { FormattedMessage } from "react-intl";
import { type ActivityDetailsFragment } from "../../../lib/graphql";
import { useCurrentUser, usePermissions } from "../../../lib/hooks/basic/users";
import { MachineCosts } from "../../machines/MachineCosts";
import { ChangeList } from "../../shared/ChangeLog/ChangeList";
import { MenuContent } from "../../shared/MenuContent";
import { ActivityBasicForm } from "./ActivityBasicForm";
import { ActivityBasicInfo } from "./ActivityBasicInfo";
import { ActivityPayments } from "./ActivityPayments";
import { ActivityWorkOrderSettings } from "./ActivityWorkOrderSettings";

interface ActivityContentProps {
  mode: "readonly" | "edit" | "new";
  activity?: ActivityDetailsFragment;
  onChange?(key: string): void;
}

export function ActivityContent({
  mode,
  activity,
  onChange,
}: ActivityContentProps) {
  const { currentTenant } = useCurrentUser();

  const disabled = mode === "new";
  const readonly = mode === "readonly";
  const showWage = usePermissions((p) => p.settings?.showWage);

  const items: Record<string, any> = {
    basic: {
      title: <FormattedMessage id="basicInfo" />,
      render: () =>
        !readonly ? (
          <Row>
            <Col md={12}>
              <ActivityBasicForm activity={activity} />
            </Col>
          </Row>
        ) : (
          activity && <ActivityBasicInfo activity={activity} />
        ),
    },
    advanced: {
      title: (
        <FormattedMessage
          id="advancedSettings"
          defaultMessage="advancedSettings"
        />
      ),
      group: "advanced",
    },
    workOrder: {
      title: <FormattedMessage id="workOrders.entityName" />,
      disabled,
      group: "advanced",
      render: () =>
        activity && (
          <ActivityWorkOrderSettings activity={activity} readonly={readonly} />
        ),
    },
    payment: {
      title: (
        <FormattedMessage
          id="activities.paymentRules"
          defaultMessage="Payment Rules"
        />
      ),
      disabled: disabled || !showWage,
      group: "advanced",
      render: () =>
        activity && (
          <ActivityPayments activity={activity} readonly={readonly} />
        ),
    },
    machineCost: {
      title: <FormattedMessage id="activities.machineCosts" />,
      disabled: disabled || !showWage,
      group: "advanced",
      render: () =>
        activity && <MachineCosts activity={activity} readonly={readonly} />,
    },
    history: {
      title: <FormattedMessage id="history" />,
      render: () =>
        activity && <ChangeList id={activity.id} type="Agro::Activity" />,
    },
  };

  if (!readonly) delete items.history;

  if (!currentTenant.features.advancedActivities) {
    delete items.advanced;
    delete items.payment;
    delete items.machineCost;
    delete items.workOrder;
  }

  return <MenuContent onChange={onChange} items={items} />;
}
